import axios from "axios";
import WorkspaceService from "@/src/api/WorkspaceService.js";
import { isPublicRoute } from "@/src/constants/routeConstants.js";
import { SERVICE_ENDPOINTS } from "./serviceEndpoints.js";

const getCSRFToken = () => {
  const csrfToken = document.querySelector('[name="csrf-token"]') ||
    document.head.querySelector('meta[name="csrf-token"]') || {
      content: "no-csrf-token",
    };
  return csrfToken.content;
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
};

const getServiceConfig = (serviceName) => {
  const env = process.env.NODE_ENV || "development";
  const endpoints = SERVICE_ENDPOINTS[serviceName];

  if (!endpoints) {
    throw new Error(`Unknown service: ${serviceName}. Available services: ${Object.keys(SERVICE_ENDPOINTS).join(", ")}`);
  }

  if (env === "production") {
    return {
      baseUrl: endpoints.production,
      requireAuth: true,
    };
  } else if (env === "staging") {
    return {
      baseUrl: endpoints.staging,
      requireAuth: true,
    };
  } else {
    return {
      baseUrl: endpoints.development,
      requireAuth: false,
    };
  }
};

const createRequestInterceptor = (requireAuth) => {
  return (config) => {
    const workspaceId = WorkspaceService.getCurrentWorkspaceId();
    if (workspaceId) {
      config.headers["X-Workspace-Id"] = workspaceId;
    }

    const userId = localStorage.getItem("userId");
    if (userId) {
      config.headers["X-USER-ID"] = userId;
    }

    if (requireAuth) {
      const xsrfToken = getCookie("XSRF-TOKEN");
      if (xsrfToken) {
        config.headers["X-XSRF-TOKEN"] = xsrfToken;
      }
    }

    return config;
  };
};

const createResponseInterceptor = (requireAuth) => {
  return {
    onSuccess: (response) => response,
    onError: (error) => {
      if (requireAuth && error.response?.status === 401) {
        const currentPath = window.location.pathname;

        if (!isPublicRoute(currentPath)) {
          setTimeout(() => {
            const loginUrl = `${window.location.origin}/auth_users?session_expired=true`;
            window.location.replace(loginUrl);
          }, 100);
        }
      }
      return Promise.reject(error);
    },
  };
};

export const createTransport = (serviceName) => {
  const { baseUrl, requireAuth } = getServiceConfig(serviceName);
  const csrfToken = getCSRFToken();

  const transport = axios.create({
    baseURL: baseUrl,
    withCredentials: requireAuth,
    headers: requireAuth
      ? {
          common: {
            "X-CSRF-Token": csrfToken,
            "Content-Type": "application/json",
          },
        }
      : {
          "Content-Type": "application/json",
        },
  });

  transport.interceptors.request.use(
    createRequestInterceptor(requireAuth),
    (error) => Promise.reject(error)
  );

  const responseInterceptor = createResponseInterceptor(requireAuth);
  transport.interceptors.response.use(
    responseInterceptor.onSuccess,
    responseInterceptor.onError
  );

  return {
    transport,
    baseUrl,
    requireAuth,
  };
};

export const registerServiceEndpoint = (serviceName, endpoints) => {
  if (SERVICE_ENDPOINTS[serviceName]) {
    console.warn(`Service ${serviceName} already exists. Overwriting.`);
  }
  SERVICE_ENDPOINTS[serviceName] = endpoints;
};

export { SERVICE_ENDPOINTS, getServiceConfig, getCookie, getCSRFToken };
